import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Table,
    Image,
    Placeholder,
    Badge,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAdd,
    faEdit,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import PageHeading from "components/shared/PageHeading";
import CustomPagination from "components/shared/Pagination";
import CreatePackageModal from "components/modals/CreatePackageModal";
import DeleteSchoolModal from "components/modals/DeleteSchoolModal";
import nodataimage from "../../../assets/nodataimage.svg";
import {
    getAllPackages,
    createPackage,
    editPackage,
    deletePackage,
} from "services/Admin/Package.service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const Packages = () => {
    const { t } = useTranslation();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [packageToDelete, setPackageToDelete] = useState(null);

    const fetchPackages = async (page = 1) => {
        setLoading(true);
        try {
            const response = await getAllPackages(page, 10);
            const data = response?.data?.results;
            setPackages(data?.packages || []);
            setTotalPages(data?.totalPages || 1);
            setCurrentPage(parseInt(data?.currentPage) || page);
        } catch (error) {
            console.error("Error fetching packages:", error);
            toast.error(t("Error fetching packages"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPackages(currentPage);
    }, [currentPage]);

    const handleShowModal = (pkg = null) => {
        setEditData(pkg);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditData(null);
    };

    const handleSavePackage = async (packageData) => {
        const toastId = toast.loading(
            editData ? t("Updating package...") : t("Creating package...")
        );

        try {
            if (editData) {
                await editPackage(editData._id, packageData);
                toast.update(toastId, {
                    render: t("Package updated successfully!"),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                });
            } else {
                await createPackage(packageData);
                toast.update(toastId, {
                    render: t("Package created successfully!"),
                    type: "success",
                    isLoading: false,
                    autoClose: 3000,
                });
            }

            handleCloseModal();
            fetchPackages(currentPage);
        } catch (error) {
            console.error("Error saving package:", error);
            toast.update(toastId, {
                render: error?.response?.data?.message || t("Error saving package"),
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
        }
    };

    const handleShowDeleteModal = (pkg) => {
        setPackageToDelete(pkg);
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setPackageToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        const toastId = toast.loading(t("Deleting package..."));

        try {
            await deletePackage(packageToDelete._id);
            toast.update(toastId, {
                render: t("Package deleted successfully!"),
                type: "success",
                isLoading: false,
                autoClose: 3000,
            });

            handleCloseDeleteModal();

            // If current page becomes empty after delete, go to previous page
            if (packages.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                fetchPackages(currentPage);
            }
        } catch (error) {
            console.error("Error deleting package:", error);
            toast.update(toastId, {
                render: error?.response?.data?.message || t("Error deleting package"),
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return (
        <>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <PageHeading heading={t("Packages Management")} />
                <Button
                    className="px-3 py-2 border-0 button-style"
                    onClick={() => handleShowModal()}
                >
                    <FontAwesomeIcon icon={faAdd} className="me-2" />
                    {t("Create Package")}
                </Button>
            </div>

            <Card>
                <Card.Body>
                    {loading ? (
                        <div>
                            {[...Array(5)].map((_, idx) => (
                                <div key={idx} className="mb-3">
                                    <Placeholder as="p" animation="glow">
                                        <Placeholder xs={12} />
                                    </Placeholder>
                                </div>
                            ))}
                        </div>
                    ) : packages.length === 0 ? (
                        <div className="py-5 text-center">
                            <Image src={nodataimage} alt="No data" />
                            <h5 className="mt-3 text-secondary">
                                {t("No packages created yet")}
                            </h5>
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>{t("Name")}</th>
                                    <th>{t("Price")}</th>
                                    <th>{t("Duration")}</th>
                                    <th>{t("Max Students")}</th>
                                    <th>{t("Max Teachers")}</th>
                                    <th>{t("Max Classes")}</th>
                                    <th>{t("Status")}</th>
                                    <th className="text-center">{t("Actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {packages.map((pkg) => (
                                    <tr key={pkg._id}>
                                        <td>
                                            <strong>{pkg.name}</strong>
                                            {pkg.description && (
                                                <div className="text-muted small">
                                                    {pkg.description.substring(0, 50)}
                                                    {pkg.description.length > 50 ? "..." : ""}
                                                </div>
                                            )}
                                        </td>
                                        <td>${pkg.price}</td>
                                        <td>{pkg.duration} {t("months")}</td>
                                        <td>{pkg.maxStudents}</td>
                                        <td>{pkg.maxTeachers}</td>
                                        <td>{pkg.maxClasses || 0}</td>
                                        <td>
                                            <Badge
                                                bg={pkg.status === "active" ? "success" : "secondary"}
                                            >
                                                {pkg.status === "active" ? t("Active") : t("Inactive")}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleShowModal(pkg)}
                                                title={t("Edit package")}
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleShowDeleteModal(pkg)}
                                                title={t("Delete package")}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {totalPages > 1 && (
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            )}

            <CreatePackageModal
                show={showModal}
                handleClose={handleCloseModal}
                onSave={handleSavePackage}
                editData={editData}
            />

            <DeleteSchoolModal
                show={showDeleteModal}
                handleClose={handleCloseDeleteModal}
                handleConfirmDelete={handleDeleteConfirm}
                isPermanent={true}
            />
        </>
    );
};

export default Packages;
